import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { createWebApiClient } from '../../api/webSession.js';
import { consumePostOnboardingReturnTo, navigateHashPath } from '../../app/return-to.js';
import { navigateTo } from '../../app/router.js';
import { showToast } from '../../app/toast.js';
import { Button, ButtonLink, Field, IconGlyph, Select, Sheet } from '../../ui/primitives/index.js';
import { BrandWord } from '../auth/AuthScreens.js';

export function OnboardingProfileScreen(props: { preview?: boolean; screenId?: string }) {
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setFailed('');
    try {
      await createWebApiClient().updateProfile({
        displayName: String(form.get('displayName') || ''),
        gender: String(form.get('gender') || 'unknown') as 'female' | 'male' | 'other' | 'unknown',
      });
      navigateTo('onboarding-birth');
    } catch {
      setFailed('Could not save right now. Please try again later.');
      setSaving(false);
    }
  }

  return (
    <OnboardingFrame stepIndex={1} title="First, a little about you" body="These basics help Mira relate to you in a way that fits you.">
      <form className="mira-auth-form" onSubmit={submit}>
        {props.preview && ['sr_04_onboarding_required', 'uf_04_finish_setup', 'ag_04_onboarding_return_notice'].includes(props.screenId || '') ? (
          <aside className="mira-soft-note"><span aria-hidden="true"><IconGlyph value="↩" /></span><p>After setup, Mira takes you back to what you were trying to do.</p></aside>
        ) : null}
        <Field className="mira-onboarding-field" icon="♙" label="What should we call you" name="displayName" placeholder="Enter the name you prefer" required />
        <Select className="mira-onboarding-field" help="Optional. Helps avoid awkward forms of address." icon="♙" label="Gender (optional)" name="gender" defaultValue="unknown">
          <option value="unknown">Prefer not to say</option>
          <option value="female">Female</option>
          <option value="male">Male</option>
          <option value="other">Other</option>
        </Select>
        {failed ? <p className="mira-form-error">{failed}</p> : null}
        <Button busy={saving} disabled={saving} type="submit">{saving ? 'Saving…' : 'Next'}</Button>
      </form>
    </OnboardingFrame>
  );
}

export function OnboardingBirthScreen(props: { screenId?: string; showSkipSheet?: boolean }) {
  const previewFailed = props.screenId === 'ob_03_birth_profile_failed';
  const [failed, setFailed] = useState(previewFailed ? 'Birth details could not be saved right now. Please try again.' : '');
  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setFailed('');
    try {
      await createWebApiClient().updateBirthProfile({
        birthDate: String(form.get('birthDate') || ''),
        birthHour: numberOrNull(form.get('birthHour')),
        birthMinute: numberOrNull(form.get('birthMinute')),
        timezone: String(form.get('timezone') || 'Asia/Shanghai'),
        birthPlace: String(form.get('birthPlace') || ''),
      });
      navigateTo('onboarding-relationship');
    } catch {
      setFailed('Birth details could not be saved. Please check your entries and try again.');
      setSaving(false);
    }
  }

  return (
    <OnboardingFrame stepIndex={2} title="Your birth details" body="Mira builds its first understanding from this. Unsure about the time? Leave it blank — it is never treated as midnight.">
      <form className="mira-auth-form" onSubmit={submit} ref={formRef}>
        <Field className="mira-onboarding-field" defaultValue={props.screenId === 'ob_01_birth_profile_ready' ? '1992-08-18' : undefined} icon="▣" label="Birth date *" name="birthDate" type="date" required />
        <Field className="mira-onboarding-field" defaultValue={props.screenId === 'ob_01_birth_profile_ready' ? '08' : undefined} icon="◷" label="Birth hour (0–23)" name="birthHour" placeholder="Leave blank if unsure" type="number" min={0} max={23} />
        <Field className="mira-onboarding-field" defaultValue={props.screenId === 'ob_01_birth_profile_ready' ? '30' : undefined} icon="◷" label="Birth minute" name="birthMinute" placeholder="Leave blank if unsure" type="number" min={0} max={59} />
        <Field className="mira-onboarding-field" icon="◎" label="Time zone" name="timezone" defaultValue="Asia/Shanghai" />
        <Field className="mira-onboarding-field" icon="⌖" label="Birth place (optional)" name="birthPlace" placeholder="City, province, country" />
        <aside className="mira-soft-note"><span aria-hidden="true"><IconGlyph value="♧" /></span><p>Date only is saved as date-known; adding the hour saves as hour-known; hour plus minute is the most precise.<em>You can change this later — every change keeps a new version.</em></p></aside>
        {failed || previewFailed ? <p className="mira-form-error">{failed || 'Birth details could not be saved right now. Please try again.'}</p> : null}
        <Button busy={saving} disabled={saving} type="submit">{saving ? 'Saving…' : 'Save & continue'} <span aria-hidden="true"><IconGlyph value="→" /></span></Button>
        <ButtonLink href="#/onboarding/birth?sheet=skip-time" variant="text">I don't know my birth time</ButtonLink>
      </form>
      {props.showSkipSheet ? <BirthTimeSkipSheet onSkip={() => skipBirthTime(formRef.current, setFailed)} /> : null}
    </OnboardingFrame>
  );
}

export function OnboardingRelationshipScreen(props: { preview?: boolean; screenId?: string }) {
  const previewSaving = props.preview && props.screenId === 'or_02_saving_relationship';
  const previewFailed = props.preview && props.screenId === 'or_03_save_failed';
  const [failed, setFailed] = useState(previewFailed ? 'This relationship could not be saved right now. Please try again.' : '');
  const [saving, setSaving] = useState(previewSaving);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setFailed('');
    try {
      await createWebApiClient().createRelationship({
        displayName: String(form.get('displayName') || ''),
        relationshipText: String(form.get('relationshipText') || ''),
        gender: String(form.get('gender') || 'unknown') as 'female' | 'male' | 'other' | 'unknown',
        birthDate: String(form.get('birthDate') || ''),
      });
      showToast('Relationship added.');
      finishOnboarding();
    } catch {
      setFailed('This relationship could not be saved right now. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className="mira-onboarding-screen mira-onboarding-relationship-screen mira-botanical-page">
      <header className="mira-onboarding-relationship-hero">
        <BrandWord />
        <p>Step 3 of 3</p>
        <div aria-hidden="true"><span /><span /><span data-active /></div>
        <h1>Add an important relationship</h1>
        <strong>If you like, add someone who matters now — or skip and add them later.</strong>
      </header>
      <details className="mira-onboarding-relationship-details">
        <summary className="mira-onboarding-add-card">
          <span aria-hidden="true"><IconGlyph value="♙" /><IconGlyph value="＋" /></span>
          <div>
            <h2>Add relationship</h2>
            <p>Write down someone who matters to you</p>
          </div>
          <b aria-hidden="true"><IconGlyph value="›" /></b>
        </summary>
        <form className="mira-auth-form mira-onboarding-relationship-form" onSubmit={submit}>
        <Field className="mira-onboarding-field" icon="♙" label="Name" name="displayName" placeholder="What do you call them" required />
        <Field className="mira-onboarding-field" icon="♡" label="Relationship" name="relationshipText" placeholder="Partner, parent, friend…" required />
        <Select className="mira-onboarding-field" icon="♙" label="Gender (optional)" name="gender" defaultValue="unknown">
          <option value="unknown">Prefer not to say</option>
          <option value="female">Female</option>
          <option value="male">Male</option>
          <option value="other">Other</option>
        </Select>
        <Field className="mira-onboarding-field" icon="▣" label="Birth date (optional)" name="birthDate" type="date" />
        {failed || previewFailed ? <p className="mira-form-error">{failed || 'This relationship could not be saved right now. Please try again.'}</p> : null}
        <Button busy={saving || previewSaving} disabled={saving || previewSaving} type="submit">{saving || previewSaving ? 'Saving…' : 'Save relationship'}</Button>
      </form>
      </details>
      <section className="mira-onboarding-example-card">
        <p>Preview</p>
        <div><span aria-hidden="true"><IconGlyph value="♧" /></span><strong>Relationship saved</strong><em>Once saved, they appear in your relationships list.</em></div>
      </section>
      <p className="mira-onboarding-relationship-quote"><span aria-hidden="true"><IconGlyph value="♧" /></span>You can add, edit, or delete relationships anytime on the Relationships page.</p>
      <button className="mira-text-action" onClick={finishOnboarding} type="button">Skip for now</button>
      <Button onClick={finishOnboarding}>Done <span aria-hidden="true"><IconGlyph value="→" /></span></Button>
    </article>
  );
}

function finishOnboarding() {
  const returnTo = consumePostOnboardingReturnTo();
  if (returnTo) navigateHashPath(returnTo);
  else navigateTo('today');
}

async function skipBirthTime(form: HTMLFormElement | null, setFailed: (message: string) => void) {
  const data = new FormData(form || undefined);
  const birthDate = String(data.get('birthDate') || '');
  if (!birthDate) {
    setFailed('Enter the birth date first, then choose to skip the time.');
    navigateTo('onboarding-birth');
    return;
  }
  try {
    await createWebApiClient().updateBirthProfile({
      birthDate,
      birthHour: null,
      birthMinute: null,
      timezone: String(data.get('timezone') || 'Asia/Shanghai'),
      birthPlace: String(data.get('birthPlace') || '') || null,
    });
    navigateTo('onboarding-relationship');
  } catch {
    setFailed('Birth date could not be saved right now. Please try again.');
    navigateTo('onboarding-birth');
  }
}

function OnboardingFrame(props: { stepIndex: 1 | 2 | 3; title: string; body: string; children: ReactNode }) {
  const birthStep = props.stepIndex === 2;
  return (
    <article className={`mira-onboarding-screen mira-onboarding-step-${props.stepIndex} mira-botanical-page`}>
      <div className="mira-leaf-wash right" aria-hidden="true" />
      <header className="mira-onboarding-header">
        <BrandWord />
        {birthStep ? (
          <>
            <div className="mira-onboarding-stepper" aria-hidden="true">
              <span data-done><IconGlyph value="✓" /></span><b /><span data-active>2</span><b /><span>3</span>
            </div>
            <p className="mira-onboarding-step-label">Step 2 of 3</p>
          </>
        ) : (
          <>
            <div className="mira-onboarding-progress" aria-hidden="true">
              <span>{props.stepIndex} / 3</span>
              <p>Step ${props.stepIndex} of 3</p>
            </div>
            <div className="mira-onboarding-progress-line" aria-hidden="true"><span style={{ width: `${(props.stepIndex / 3) * 100}%` }} /></div>
          </>
        )}
        <h1>{props.title}</h1>
        <strong>{props.body}</strong>
      </header>
      {props.children}
    </article>
  );
}

function BirthTimeSkipSheet(props: { onSkip: () => void }) {
  return (
    <Sheet className="mira-skip-sheet mira-birth-skip-sheet" title="Skip birth time for now">
        <div className="mira-round-icon"><IconGlyph value="◷" /></div>
        <h2>Don't know the exact time?</h2>
        <p>Hour and minute make understanding sharper, but unknown is unknown — Mira never defaults it to midnight.</p>
        <p>Continue with date-known for now and add it anytime later. <span aria-hidden="true"><IconGlyph value="♥" /></span></p>
        <Button onClick={() => navigateTo('onboarding-birth')}>Keep filling in</Button>
        <Button tone="secondary" onClick={props.onSkip}>Save date only</Button>
    </Sheet>
  );
}

function numberOrNull(value: FormDataEntryValue | null): number | null {
  const number = Number(value);
  return Number.isFinite(number) && value !== '' && value !== null ? number : null;
}
